// A skill's files as the editor edits them (D62): the path rules the API enforces (relative,
// `A-Z a-z 0-9 . _ / -`, at most 200 characters, no empty, `.` or `..` segment), `SKILL.md`
// always present, and the tree the file list draws. Pure, so the rules are tested on their own.
import { messages } from "../i18n";
import type { EditableFile } from "./api";

export const SKILL_MD = "SKILL.md";
export const MAX_FILE_BYTES = 256 * 1024;

const ALLOWED = /^[A-Za-z0-9._/-]+$/;

/** Why `path` cannot be a skill file's path, or undefined when it can. */
export function pathProblem(path: string): string | undefined {
  const t = messages().skills.path;
  if (path === "") return t.empty;
  if (path.length > 200) return t.tooLong;
  if (!ALLOWED.test(path)) return t.characters;
  if (path.split("/").some((s) => s === "" || s === "." || s === "..")) return t.segments;
  return undefined;
}

export type Edit = { ok: true; files: EditableFile[] } | { ok: false; error: string };

const byPath = (files: EditableFile[]) => [...files].sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));

export function addFile(files: EditableFile[], path: string, content = ""): Edit {
  const problem = pathProblem(path) ?? (files.some((f) => f.path === path) ? messages().skills.path.taken : undefined);
  return problem ? { ok: false, error: problem } : { ok: true, files: byPath([...files, { path, content }]) };
}

export function renameFile(files: EditableFile[], from: string, to: string): Edit {
  if (from === to) return { ok: true, files };
  const t = messages().skills.path;
  if (from === SKILL_MD) return { ok: false, error: t.renameSkillMd };
  const problem = pathProblem(to) ?? (files.some((f) => f.path === to) ? t.taken : undefined);
  if (problem) return { ok: false, error: problem };
  return { ok: true, files: byPath(files.map((f) => (f.path === from ? { ...f, path: to } : f))) };
}

export function deleteFile(files: EditableFile[], path: string): Edit {
  if (path === SKILL_MD) return { ok: false, error: messages().skills.path.deleteSkillMd };
  return { ok: true, files: files.filter((f) => f.path !== path) };
}

export function setContent(files: EditableFile[], path: string, content: string): EditableFile[] {
  return files.map((f) => (f.path === path ? { ...f, content } : f));
}

/** UTF-8 size, what the 256 KB limit counts. */
export const byteSize = (text: string): number => new TextEncoder().encode(text).length;

export const isMarkdown = (path: string): boolean => /\.(md|markdown)$/i.test(path);

export interface TreeRow {
  kind: "dir" | "file";
  /** The last segment. */
  name: string;
  /** The full path (a directory's without a trailing slash). */
  path: string;
  depth: number;
}

/** Folders and files in display order: `SKILL.md` first, then folders before files, by name. */
export function tree(files: EditableFile[]): TreeRow[] {
  const rows: TreeRow[] = [];
  const seen = new Set<string>();
  const sorted = [...files].sort((a, b) => order(a.path).localeCompare(order(b.path)));
  for (const f of sorted) {
    const parts = f.path.split("/");
    parts.slice(0, -1).forEach((name, depth) => {
      const path = parts.slice(0, depth + 1).join("/");
      if (!seen.has(path)) {
        seen.add(path);
        rows.push({ kind: "dir", name, path, depth });
      }
    });
    rows.push({ kind: "file", name: parts[parts.length - 1] ?? f.path, path: f.path, depth: parts.length - 1 });
  }
  return rows;
}

// Sort key: SKILL.md first; within a folder, sub-folders (prefixed "0") before files ("1").
function order(path: string): string {
  if (path === SKILL_MD) return "";
  const parts = path.split("/");
  return parts.map((p, i) => (i < parts.length - 1 ? `0${p}` : `1${p}`)).join("/");
}
